import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { SegmentedToggle } from "@/components/sales-report/components/SegmentedToggle"
import { MetricDeltas } from "@/components/sales-report/components/MetricDeltas"
import { InfoPopover } from "@/components/InfoPopover"
import { Dropdown } from "@/components/cash-collections/components/Dropdown"
import { fmt, MONO, monthLabel } from "@/components/sales-report/components/format"
import type { CashCollectionsData } from "@/components/cash-collections"
import type { RevenueMeasures, SalesReportData } from "@/components/sales-report"
import type { DSDCenterData } from "@/components/dsd-center"

export interface OverviewProps {
  cashCollections: CashCollectionsData
  salesReport: SalesReportData
  dsdCenter: DSDCenterData
}

type View = "EUR" | "USD" | "Consolidated"
const VIEWS: View[] = ["EUR", "USD", "Consolidated"]
type Range = "1M" | "3M" | "6M" | "12M" | "YTD"
const RANGES: Range[] = ["1M", "3M", "6M", "12M", "YTD"]
type Basis = "By payment" | "By invoiced"
const BASES: Basis[] = ["By payment", "By invoiced"]
type CentreBasis = "Order month" | "Invoiced month"
const CENTRE_BASES: CentreBasis[] = ["Order month", "Invoiced month"]
type OrderKey = "trueSales" | "cashCollected" | "outstanding" | "revenue" | "pipeline" | "pcCredit"

type Tone = "emerald" | "amber" | "champagne" | "stone"
const TONE_BAR: Record<Tone, string> = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  champagne: "bg-champagne-500",
  stone: "bg-stone-400 dark:bg-stone-600",
}
const TONE_DOT: Record<Tone, string> = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  champagne: "bg-champagne-500",
  stone: "bg-stone-400 dark:bg-stone-500",
}

/** 17-month True Sales trend rail under the hero figure. Lightweight inline SVG. */
function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const w = 260
  const h = 40
  const span = max - min || 1
  const pts = data
    .map((v, i) => {
      const x = (i / Math.max(1, data.length - 1)) * w
      const y = h - ((v - min) / span) * h
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(" ")
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="mt-5 h-10 w-full text-champagne-500" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

/**
 * SONAR Overview — the command-centre. A page-level period / range / currency control
 * drives the whole page: a hero True Sales figure beside the three-lens bridge (Zone 1),
 * a revenue-by-centre strip (Zone 2), a risk + efficiency band (Zone 3), and a fenced
 * PC-credit memo (Zone 4). Currencies never blend as raw sums (USD consolidates at each
 * month's locked FX); PC credit is a memo, never inside a sales total. Read-only.
 */
export function Overview({ cashCollections, salesReport, dsdCenter }: OverviewProps) {
  const months = salesReport.months
  const lastIdx = months.length - 1
  const [view, setView] = useState<View>("Consolidated")
  const [range, setRange] = useState<Range>("1M")
  const [endKey, setEndKey] = useState<string>(months[lastIdx])
  const [basis, setBasis] = useState<Basis>("By payment")
  const [centreBasis, setCentreBasis] = useState<CentreBasis>("Order month")

  const isCon = view === "Consolidated"
  const displayCurrency = view === "USD" ? "USD" : "EUR"
  const fmtV = (v: number) => fmt(v, displayCurrency)
  const endIdx = Math.max(0, months.indexOf(endKey))

  // Consolidated-aware monthly order-basis value.
  const valAt = (i: number, key: OrderKey): number => {
    if (i < 0 || i > lastIdx) return 0
    const e = salesReport.data.EUR.byMonth[i]?.order[key] ?? 0
    const u = salesReport.data.USD.byMonth[i]?.order[key] ?? 0
    if (view === "EUR") return e
    if (view === "USD") return u
    const r = salesReport.fxRates[months[i]]
    return e + (r ? u / r : 0) // Consolidated €
  }

  // Window length for the selected range, ending at endIdx.
  const rangeLen = (): number => {
    if (range !== "YTD") return { "1M": 1, "3M": 3, "6M": 6, "12M": 12 }[range]
    const yr = months[endIdx].slice(-2)
    let start = endIdx
    while (start > 0 && months[start - 1].slice(-2) === yr) start--
    return endIdx - start + 1
  }
  const len = rangeLen()
  const idxRange = (end: number, n: number): number[] => {
    const out: number[] = []
    for (let i = end - n + 1; i <= end; i++) if (i >= 0 && i <= lastIdx) out.push(i)
    return out
  }
  const sumWin = (w: number[], key: OrderKey): number => w.reduce((s, i) => s + valAt(i, key), 0)

  const win = idxRange(endIdx, len)
  const trueSales = sumWin(win, "trueSales")
  const settled = sumWin(win, "cashCollected")
  const outstanding = sumWin(win, "outstanding")
  const pipeline = sumWin(win, "pipeline")

  // Prior-window + YoY (null when the comparison window runs off the start of the data).
  const priorOk = endIdx - 2 * len + 1 >= 0
  const yoyOk = endIdx - 12 - len + 1 >= 0
  const priorWin = idxRange(endIdx - len, len)
  const yoyWin = idxRange(endIdx - 12, len)
  const prevTrue = priorOk ? sumWin(priorWin, "trueSales") : null
  const yoyTrue = yoyOk ? sumWin(yoyWin, "trueSales") : null
  const prevOutstanding = priorOk ? sumWin(priorWin, "outstanding") : null

  const spanLabel = (w: number[]): string =>
    w.length === 0
      ? "—"
      : w.length === 1
        ? monthLabel(months[w[0]])
        : `${monthLabel(months[w[0]])} – ${monthLabel(months[w[w.length - 1]])}`
  const periodLabel = spanLabel(win)
  const priorLabel = priorOk ? spanLabel(priorWin) : "—"
  const yoyLabel = yoyOk ? spanLabel(yoyWin) : ""

  // Fenced AR-basis net cash banked (€, real from Apr 2026), matched by month key.
  const cashByKey: Record<string, CashCollectionsData["months"][number]> = Object.fromEntries(
    cashCollections.months.map((m) => [m.key, m]),
  )
  const winCash = win.map((i) => cashByKey[months[i]]).filter(Boolean) as CashCollectionsData["months"]
  const netCashBanked = winCash.reduce((s, m) => s + m.netCash, 0)
  const costToCollect = winCash.reduce((s, m) => s + m.cost, 0)
  const cashCovered = winCash.length > 0

  // 17-month True Sales rail (consolidated-aware).
  const spark = months.map((_, i) => valAt(i, "trueSales"))

  // Bridge segments by basis. Both decompose the order-month True Sale.
  const seg: { label: string; sub: string; value: number; tone: Tone; term: string }[] =
    basis === "By payment"
      ? [
          { label: "Settled", sub: "card + invoice", value: settled, tone: "emerald", term: "cashCollected" },
          { label: "Outstanding", sub: "billed, not yet collected", value: outstanding, tone: "amber", term: "outstanding" },
        ]
      : [
          { label: "Revenue (Inv)", sub: "recognised / invoiced portion", value: Math.max(0, trueSales - pipeline), tone: "champagne", term: "revenue" },
          { label: "Pipeline (Pipe)", sub: "ordered, not yet invoiced", value: pipeline, tone: "stone", term: "pipeline" },
        ]
  const bridgeCaption =
    basis === "By payment"
      ? `Settled + Outstanding add back to ${fmtV(trueSales)} — the same True Sale, split by payment status (ties every month).`
      : `Revenue (Inv) + Pipeline add back to ${fmtV(trueSales)} — the same True Sale, split by invoice status (ties at the period total).`
  // True Sale is always the 100% anchor; both splits decompose it (Payment ties every
  // month, Invoiced/Pipeline ties by construction). order.revenue is the recognition-MONTH
  // figure (different timing basis) and intentionally not used here.
  const bridgeTotal = Math.max(trueSales, 1)

  // State-of-the-month sentence — deterministic; only magnitude/direction come from data.
  const outPct = prevOutstanding ? Math.round((outstanding / prevOutstanding - 1) * 100) : null
  const priorWord = len === 1 ? "month" : "window"
  const sentenceParts = [`In ${periodLabel}, True Sales were ${fmtV(trueSales)} — orders booked that month.`]
  if (cashCovered && view !== "USD") sentenceParts.push(`${fmt(netCashBanked, "EUR")} was banked in cash.`)
  if (outPct != null) {
    sentenceParts.push(
      Math.abs(outPct) < 5
        ? `Outstanding held steady vs the prior ${priorWord}.`
        : `Outstanding ${outPct > 0 ? "rose" : "fell"} ${Math.abs(outPct)}% vs the prior ${priorWord}.`,
    )
  }
  const sentence = sentenceParts.join(" ")

  // Newest month first (May 2026 at the top, Jan 2025 at the bottom).
  const monthOpts = months.map((m) => ({ value: m, label: monthLabel(m) })).reverse()

  // --- Zone 2: revenue by business unit (arrays align to monthsInYtd — 2026 only). ---
  const ytdMonths = salesReport.data.EUR.monthsInYtd
  const ytdWin = win.map((i) => ytdMonths.indexOf(months[i])).filter((j) => j >= 0)
  const priorYtdWin = priorWin.map((i) => ytdMonths.indexOf(months[i])).filter((j) => j >= 0)
  const unitMeasure: "ord" | "inv" = centreBasis === "Order month" ? "ord" : "inv"
  const unitsE = salesReport.data.EUR.revenueByUnit
  const unitsU = salesReport.data.USD.revenueByUnit
  const hasUnits = !!(unitsE || unitsU)
  // Prior-year (YoY): same window a year back. The *PY arrays align to monthsInYtd, so we sum
  // them over the same indices; USD legs convert at the prior-year month's locked FX. Absent on
  // older bundles → unitHasPY is false and the YoY delta hides itself.
  const PY_MEASURE = { ord: "ordPY", inv: "invPY", pipe: "pipePY" } as const
  const pyMonth = (m: string) => m.replace(/-(\d{2})$/, (_, y) => "-" + String(Number(y) - 1).padStart(2, "0"))
  const unitHasPY = (unitsE ?? unitsU ?? []).some((u) => (u.ordPY?.length ?? 0) > 0)
  // Sum one node's measure over a window, both currencies, consolidating at each month's locked FX.
  const nodeSum = (e: RevenueMeasures | undefined, u: RevenueMeasures | undefined, measure: "ord" | "inv" | "pipe", ytdIdx: number[], py = false): number =>
    ytdIdx.reduce((s, j) => {
      const key = py ? PY_MEASURE[measure] : measure
      const ev = e?.[key]?.[j] ?? 0
      const uv = u?.[key]?.[j] ?? 0
      if (view === "EUR") return s + ev
      if (view === "USD") return s + uv
      const r = salesReport.fxRates[py ? pyMonth(ytdMonths[j]) : ytdMonths[j]]
      return s + ev + (r ? uv / r : 0)
    }, 0)
  const nodeRow = (e: RevenueMeasures | undefined, u: RevenueMeasures | undefined) => {
    const value = nodeSum(e, u, unitMeasure, ytdWin)
    const prior = priorYtdWin.length ? nodeSum(e, u, unitMeasure, priorYtdWin) : null
    const pipe = nodeSum(e, u, "pipe", ytdWin)
    const mom = prior ? Math.round((value / prior - 1) * 100) : null
    const py = unitHasPY && ytdWin.length ? nodeSum(e, u, unitMeasure, ytdWin, true) : null
    const yoy = py && py > 0 ? Math.round((value / py - 1) * 100) : null
    return { value, pipe, mom, yoy }
  }
  const unionIds = <T extends { id: string }>(a: T[] | undefined, b: T[] | undefined): string[] => {
    const ids = (a ?? []).map((x) => x.id)
    ;(b ?? []).forEach((x) => {
      if (!ids.includes(x.id)) ids.push(x.id)
    })
    return ids
  }
  const unitCards = unionIds(unitsE, unitsU).map((id) => {
    const e = unitsE?.find((x) => x.id === id)
    const u = unitsU?.find((x) => x.id === id)
    const departments = unionIds(e?.departments, u?.departments).map((did) => {
      const ed = e?.departments.find((d) => d.id === did)
      const ud = u?.departments.find((d) => d.id === did)
      return { id: did, name: ed?.name ?? ud?.name ?? did, ...nodeRow(ed, ud) }
    })
    return { id, name: e?.name ?? u?.name ?? id, ...nodeRow(e, u), departments }
  })
  const unitTotal = unitCards.reduce((s, r) => s + r.value, 0)
  const unitPeriod = ytdWin.length ? spanLabel(ytdWin.map((j) => months.indexOf(ytdMonths[j]))) : "—"
  // revenueByUnit only covers 2026 (monthsInYtd); when the selected window reaches
  // before Jan-26 the strip can only show its 2026 portion, so its Total will not
  // tie to the hero — flag that rather than let it read as a broken tie.
  const unitPartial = ytdWin.length > 0 && ytdWin.length < win.length
  // A compact ▲/▼ delta for the unit cards and department chips.
  const pctSpan = (pct: number | null, title: string, extra = "") =>
    pct == null ? (
      <span className={cn("text-stone-300 dark:text-stone-600", extra)}>—</span>
    ) : (
      <span className={cn(pct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400", extra)} title={title}>
        {pct >= 0 ? "▲" : "▼"} {Math.abs(pct) >= 1000 ? "999+" : Math.abs(pct)}%
      </span>
    )

  // --- Zone 3: risk + efficiency band. ---
  const grossCash = winCash.reduce((s, m) => s + m.gross, 0)
  const feesSum = winCash.reduce((s, m) => s + m.fees, 0)
  const fxSum = winCash.reduce((s, m) => s + m.fx, 0)
  const refundsSum = winCash.reduce((s, m) => s + m.refunds, 0)
  const costPct = grossCash ? (costToCollect / grossCash) * 100 : 0
  const cashPartial = cashCovered && winCash.length < win.length
  const mover = cashCollections.movers?.[0]
  const outstandingRising = outPct != null && outPct > 0

  // --- Zone 4: fenced PC-credit memo (17-month axis, same as the window). ---
  // Utilisation is a point-in-time STOCK ratio (used vs the standing credit line), so it is
  // read at the period-ending month — never summed over the window (summing a balance across
  // months is meaningless). PC credit consumed below IS a flow, so it is windowed.
  const utilAt = (field: "used" | "madeAvailable"): number => {
    const e = dsdCenter.data.EUR.utilisation[field][endIdx] ?? 0
    const u = dsdCenter.data.USD.utilisation[field][endIdx] ?? 0
    if (view === "EUR") return e
    if (view === "USD") return u
    const r = dsdCenter.fxRates[months[endIdx]]
    return e + (r ? u / r : 0)
  }
  const creditUsed = utilAt("used")
  const creditAvail = utilAt("madeAvailable")
  const utilPct = creditAvail ? Math.round((creditUsed / creditAvail) * 100) : 0
  // Over-draw flag matches the figure shown: the (consolidated) used ÷ made-available for
  // the window. Switch to EUR to surface the EUR ledger's own over-draw when it is masked
  // by USD headroom in the consolidated blend.
  const overDraw = utilPct > 100
  const pcCreditConsumed = sumWin(win, "pcCredit")

  return (
    <div className="space-y-5">
      {/* Zone 0 — control bar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
            <span className="font-medium text-stone-700 dark:text-stone-200">{periodLabel}</span> ·{" "}
            <span className="font-medium">{isCon ? "Consolidated €" : view}</span> · vs {priorLabel}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <SegmentedToggle options={RANGES} value={range} onChange={setRange} ariaLabel="Range" />
          <Dropdown label="Period ending" value={endKey} options={monthOpts} onChange={setEndKey} />
          <SegmentedToggle options={VIEWS} value={view} onChange={setView} ariaLabel="Currency" />
        </div>
      </div>

      {/* Zone 0 — state-of-the-month sentence */}
      <div className="flex items-start gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 dark:border-stone-800 dark:bg-stone-900">
        <span className="flex h-5 shrink-0 items-center text-champagne-600 dark:text-champagne-400" aria-hidden>
          ▸
        </span>
        <p className="text-sm text-stone-700 dark:text-stone-200">{sentence}</p>
      </div>

      {/* Zone 1 — hero + three-lens bridge */}
      <div className="grid gap-4 lg:grid-cols-12">
        {/* Hero */}
        <div className="relative flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white p-6 dark:border-stone-800 dark:bg-stone-900 lg:col-span-5">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-champagne-500/10 blur-2xl" aria-hidden />
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">
            <span className="h-2 w-2 rounded-full bg-champagne-500" aria-hidden />
            True Sales
            <InfoPopover term="trueSale" />
            <span className="font-normal normal-case tracking-normal text-stone-400">· {periodLabel}</span>
          </p>
          <p className={cn("mt-3 text-4xl font-semibold tracking-tight sm:text-5xl", MONO)}>{fmtV(trueSales)}</p>
          <MetricDeltas
            current={trueSales}
            prevMonth={prevTrue}
            prevYear={yoyTrue}
            prevYearLabel={yoyLabel}
            currency={displayCurrency}
            size="lg"
          />
          <Sparkline data={spark} />
          <p className="mt-2 text-xs text-stone-400 dark:text-stone-500">
            17-month trend · New collectable sales booked, Gross minus PC credit.
          </p>
        </div>

        {/* Three-lens bridge */}
        <div className="flex flex-col rounded-2xl border border-stone-200 bg-white p-6 dark:border-stone-800 dark:bg-stone-900 lg:col-span-7">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">Three-lens bridge</p>
            <div className="flex items-center gap-1.5">
              <SegmentedToggle options={BASES} value={basis} onChange={setBasis} ariaLabel="Bridge basis" />
              <InfoPopover term={basis === "By payment" ? "splitByPayment" : "splitByRecognition"} />
            </div>
          </div>

          <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">
            {trueSales > 0 ? (
              <>
                True Sale <span className={cn("font-semibold text-stone-800 dark:text-stone-100", MONO)}>{fmtV(trueSales)}</span> = 100%{" "}
                <span className="text-stone-400 dark:text-stone-500">— split two ways</span>
              </>
            ) : (
              <span className="text-stone-400">No bookings in this period.</span>
            )}
          </p>

          {/* the bar */}
          <div className="mt-2 flex h-9 w-full overflow-hidden rounded-lg">
            {seg.map((s) => (
              <div
                key={s.label}
                style={{ width: `${(s.value / bridgeTotal) * 100}%` }}
                className={cn("min-w-[2px]", TONE_BAR[s.tone])}
                title={`${s.label} · ${fmtV(s.value)}`}
              />
            ))}
          </div>

          {/* segment legend */}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {seg.map((s) => (
              <div key={s.label} className="flex items-baseline gap-2">
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", TONE_DOT[s.tone])} aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm">
                    <span className="font-medium text-stone-700 dark:text-stone-200">{s.label}</span>
                    <InfoPopover term={s.term} />{" "}
                    <span className={cn("text-stone-800 dark:text-stone-100", MONO)}>{fmtV(s.value)}</span>{" "}
                    <span className="text-stone-400 dark:text-stone-500">({Math.round((s.value / bridgeTotal) * 100)}%)</span>
                  </p>
                  <p className="text-xs text-stone-400 dark:text-stone-500">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {trueSales > 0 ? <p className="mt-3 text-xs text-stone-400 dark:text-stone-500">{bridgeCaption}</p> : null}

          {/* fenced AR-basis bank figure */}
          <div className="mt-auto border-t border-dashed border-stone-200 pt-3 dark:border-stone-700">
            {cashCovered ? (
              <p className="text-xs text-stone-400 dark:text-stone-500">
                <span className="font-medium text-stone-600 dark:text-stone-300">Net cash banked </span>
                <span className={cn("text-stone-700 dark:text-stone-200", MONO)}>{fmt(netCashBanked, "EUR")}</span> − cost to collect{" "}
                <span className={MONO}>{fmt(costToCollect, "EUR")}</span>. AR basis, € · banked this period against all prior bookings —
                different timing basis, does not tie to the bar above.
              </p>
            ) : (
              <p className="text-xs text-stone-400 dark:text-stone-500">
                Net cash banked: bank-level data starts Apr 2026 (none in this window).
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Zone 2 — revenue by business unit */}
      <div className="rounded-2xl border border-stone-200 bg-white p-6 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">
            Revenue by business unit <span className="font-normal normal-case tracking-normal text-stone-400">· {unitPeriod}</span>
            <InfoPopover term="businessUnit" />
          </p>
          <div className="flex items-center gap-1.5">
            <SegmentedToggle options={CENTRE_BASES} value={centreBasis} onChange={setCentreBasis} ariaLabel="Unit basis" />
            <InfoPopover term="recognitionVsOrderToggle" />
          </div>
        </div>
        {!hasUnits ? (
          <p className="mt-4 text-sm text-stone-400 dark:text-stone-500">
            This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see it.
          </p>
        ) : ytdWin.length === 0 ? (
          <p className="mt-4 text-sm text-stone-400 dark:text-stone-500">Revenue by business unit is available for 2026 (year-to-date). Choose a period in 2026.</p>
        ) : (
          <>
            {unitPartial ? (
              <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                Revenue by business unit covers 2026 only ({unitPeriod}); the period above also includes earlier months, so this Total intentionally won&apos;t match the hero True Sales.
              </p>
            ) : null}
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {unitCards.map((card) => (
                <div key={card.id} data-unit-card={card.id} className="rounded-xl border border-stone-100 bg-stone-50/50 p-4 dark:border-stone-800 dark:bg-stone-800/30">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-stone-800 dark:text-stone-100">
                      <span className="truncate">{card.name}</span>
                      {card.pipe > 0 ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-champagne-500" title="carries pipeline" aria-hidden /> : null}
                    </p>
                    <p className={cn("shrink-0 text-base font-semibold text-stone-800 dark:text-stone-100", MONO)}>{fmtV(card.value)}</p>
                  </div>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-400 dark:text-stone-500">
                    <span>
                      {pctSpan(card.mom, `${card.mom ?? 0}% vs prior ${win.length <= 1 ? "month" : "period"}`)} {win.length <= 1 ? "vs prev mo" : "vs prev"}
                    </span>
                    {unitHasPY ? <span>{pctSpan(card.yoy, `${card.yoy ?? 0}% vs same period last year`)} vs last yr</span> : null}
                    {card.pipe > 0 ? <span className={MONO}>pipe {fmtV(card.pipe)}</span> : null}
                    <span className={MONO}>{unitTotal ? Math.round((card.value / unitTotal) * 100) : 0}% of total</span>
                  </p>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800">
                    <div className="h-full rounded-full bg-champagne-500" style={{ width: `${unitTotal ? (card.value / unitTotal) * 100 : 0}%` }} />
                  </div>
                  {/* Department chips — each opens the Sales Report with that department expanded */}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {card.departments.map((d) => (
                      <Link
                        key={d.id}
                        href={`/sales-report?department=${d.id}`}
                        title={`Open the Sales Report with ${d.name} expanded`}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                          d.value > 0
                            ? "border-stone-200 bg-white text-stone-700 hover:border-champagne-300 hover:bg-champagne-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200 dark:hover:border-champagne-500/40 dark:hover:bg-champagne-500/10"
                            : "border-dashed border-stone-200 bg-transparent text-stone-400 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-500 dark:hover:bg-stone-800/50",
                        )}
                      >
                        <span className="font-medium">{d.name}</span>
                        <span className={MONO}>{fmtV(d.value)}</span>
                        {unitHasPY && d.value > 0 ? pctSpan(d.yoy, `${d.yoy ?? 0}% vs same period last year`, "text-[10px]") : null}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-stone-100 pt-2 text-sm dark:border-stone-800">
              <span className="font-semibold text-stone-700 dark:text-stone-200">Total</span>
              <span className={cn("font-semibold text-stone-800 dark:text-stone-100", MONO)}>{fmtV(unitTotal)}</span>
            </div>
          </>
        )}
      </div>

      {/* Zone 3 — risk + efficiency band */}
      <div className="grid gap-4 sm:grid-cols-3">
        {/* Outstanding */}
        <div
          className={cn(
            "rounded-2xl border p-5",
            outstandingRising
              ? "border-amber-300 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20"
              : "border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900",
          )}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">Outstanding · AR risk</p>
          <p className={cn("mt-2 text-2xl font-semibold tracking-tight", MONO)}>{fmtV(outstanding)}</p>
          <p className="mt-1 text-sm">
            {outPct == null ? (
              <span className="text-stone-400">no prior period</span>
            ) : (
              <span className={outPct > 0 ? "text-amber-700 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}>
                {outPct > 0 ? "▲" : "▼"} {Math.abs(outPct)}% vs prior
              </span>
            )}
          </p>
          <p className="mt-2 text-xs text-stone-400 dark:text-stone-500">Booked, not yet collected.</p>
        </div>

        {/* Cost to collect */}
        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">Cost to collect</p>
          {cashCovered ? (
            <>
              <p className={cn("mt-2 text-2xl font-semibold tracking-tight", MONO)}>{fmt(costToCollect, "EUR")}</p>
              <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">{costPct.toFixed(1)}% of gross inflows</p>
              <p className="mt-2 text-xs text-stone-400 dark:text-stone-500">
                fees {fmt(feesSum, "EUR")} · FX {fmt(fxSum, "EUR")} · refunds {fmt(refundsSum, "EUR")}
              </p>
              {cashPartial ? (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  Apr 2026+ only — {winCash.length} of {win.length} months have bank data.
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-sm text-stone-400 dark:text-stone-500">Bank-level data starts Apr 2026 — none in this window.</p>
          )}
        </div>

        {/* Biggest mover */}
        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">Biggest mover</p>
          {mover ? (
            <>
              <p className="mt-2 text-sm font-semibold text-stone-800 dark:text-stone-100">{mover.title}</p>
              <p className="mt-0.5 text-sm">
                <span className={mover.dir === "gain" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}>
                  {mover.dir === "gain" ? "▲" : "▼"} {mover.val}
                </span>{" "}
                <span className="text-stone-400">· {mover.tag}</span>
              </p>
              <p className="mt-2 line-clamp-3 text-xs text-stone-400 dark:text-stone-500">{mover.desc}</p>
            </>
          ) : (
            <p className="mt-2 text-sm text-stone-400 dark:text-stone-500">No mover data in this window.</p>
          )}
        </div>
      </div>

      {/* Zone 4 — fenced PC-credit memo */}
      <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 p-4 dark:border-stone-700 dark:bg-stone-900/40">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400 dark:text-stone-500">memo — not in sales</span>
          {creditAvail > 0 ? (
            <>
              <span className="text-stone-600 dark:text-stone-300">
                PC credit utilisation
                <InfoPopover term="creditMadeAvailable" /> <span className={cn("font-semibold", MONO)}>{utilPct}%</span>
                <span className="text-stone-400 dark:text-stone-500"> as of {monthLabel(endKey)}</span>
                {overDraw ? <span className="ml-1.5 text-amber-700 dark:text-amber-400">⚠ over-draw</span> : null}
              </span>
              <span className="text-stone-400 dark:text-stone-500">
                used <span className={MONO}>{fmt(creditUsed, "EUR")}</span> / available <span className={MONO}>{fmt(creditAvail, "EUR")}</span>
              </span>
            </>
          ) : (
            <span className="text-stone-500 dark:text-stone-400">PC credit line not allocated this period</span>
          )}
          <span className="text-stone-400 dark:text-stone-500">
            PC credit consumed
            <InfoPopover term="pcCredit" /> <span className={MONO}>{fmtV(pcCreditConsumed)}</span>
          </span>
          <Link
            href="/dsd-center"
            className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-champagne-700 transition-colors hover:text-champagne-800 dark:text-champagne-300 dark:hover:text-champagne-200"
          >
            Open DSD Center
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Section links */}
      <div className="flex flex-wrap gap-2">
        {[
          ["/sales-report", "Sales Report"],
          ["/cash-collections", "Cash Collections"],
          ["/dsd-center", "DSD Center"],
        ].map(([href, label]) => (
          <Link
            key={href}
            href={href}
            className="inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-champagne-700 transition-colors hover:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:text-champagne-300 dark:hover:bg-stone-800"
          >
            {label}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ))}
      </div>
    </div>
  )
}

export default Overview
